import { useEffect, useMemo, useState } from 'react';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useMutationExtractDelayDriverCsv } from 'api/mutations/extractDelayDriverCsv.mutation';
import { useQueryActivities } from 'api/queries/getDelayDriver.query';

import {
    FilterNames,
    filterMapper,
    getFiltersData,
} from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { statusFilterInitialState } from 'components/widgets/FinishDateFuturePotentialDelayDrivers/components/FinishDateFuturePotentialDelayDrivers.utils';

export const useFinishDateFuturePotentialDelayDriversProvider = ({
    widgetId,
    localFilters,
    projectId,
    contractId,
    globalFilterData,
    latestVersionId,
    table,
}) => {
    const isInPinboard = Boolean(widgetId);

    const idList = useMemo(() => (latestVersionId ? [latestVersionId] : []), [latestVersionId]);
    const [statusFilter, setStatusFilter] = useState<readonly ISelectOption<string>[]>(statusFilterInitialState);
    const [isCriticalPath, setIsCriticalPath] = useState<boolean>(false);
    const [isDelayDriver, setIsDelayDriver] = useState<boolean>(true);
    const [typeFilter, setTypeFilter] = useState<readonly ISelectOption<string>[]>([]);
    const [onSearch, setOnSearch] = useState<string>('');
    const [dateFilter, setDateFilter] = useState<ISelectOption<string> | null>(null);

    const pinboardState = localFilters;
    const pageState = {
        ...globalFilterData,
        statusFilter,
        isCriticalPath,
        isDelayDriver,
        typeFilter,
        onSearch,
        dateFilter,
        projectId,
        contractId,
        latestVersionId,
        sorting: table.sorting,
    };

    const source = isInPinboard ? pinboardState : pageState;

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags
                    ?.filter((item) => !item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                    ?.filter((item) => item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
                [FilterNames['START_DATE']]:
                    source?.dateFilter && source?.dateFilter.value !== 'All Months' ? [source?.dateFilter.value] : [],
                [FilterNames.NAME_SEARCH]: source?.onSearch?.length > 0 ? [onSearch] : [],
                [FilterNames.OPTIONS_STATUS]: source?.statusFilter?.map((item) => item.value).map(filterMapper),
                [FilterNames.OPTIONS_TYPE]: source?.isCriticalPath
                    ? [...(source?.typeFilter ?? []).map((item) => filterMapper(item.value)), 'CRITICAL_PATH']
                    : source?.typeFilter?.map((item) => item.value).map(filterMapper),
                [FilterNames.DELAY_DRIVER]: source?.isDelayDriver ? ['true'] : ['false'],
            }),
        [
            globalFilterData,
            source.dateFilter,
            source.typeFilter,
            source.statusFilter,
            source.onSearch,
            source.isCriticalPath,
            source.isDelayDriver,
        ],
    );

    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        wbs: source?.wbs,
        dateFilter: source?.dateFilter && source?.dateFilter.label,
        statusFilter: source?.statusFilter?.map((item) => item.label),
        typeFilter: source?.isCriticalPath
            ? [...(source?.typeFilter ?? []).map((item) => item.label), 'Critical Path']
            : source?.typeFilter?.map((item) => item.label),
        isDelayDriver: source?.isDelayDriver ? 'True' : 'False',
        onSearch: source?.onSearch,
    };

    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    return {
        ui: {
            state: getState({ source }),
            setters: {
                setStatusFilter,
                setIsCriticalPath,
                setIsDelayDriver,
                setTypeFilter,
                setOnSearch,
                setDateFilter,
            },
            pills,
        },
        api: {
            getActivities: useQueryActivities(getActivitiesPayload({ filters, idList, table, source })),
            extractDelayDriverToCsv: {
                instance: useMutationExtractDelayDriverCsv(),
                payload: createExportDelayDriverToCsvPayload({ filters, latestVersionId }),
            },
        },
    };
};

const getState = ({ source }) => {
    return {
        // global filter
        activityCodes: source?.activityCodes,
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags,
        wbs: source?.wbs,
        latestVersionId: source?.latestVersionId,

        // local state
        dateFilter: source?.dateFilter,
        onSearch: source?.onSearch,
        statusFilter: source?.statusFilter,
        isCriticalPath: source?.isCriticalPath,
        isDelayDriver: source?.isDelayDriver,
        typeFilter: source?.typeFilter,

        //table
        sorting: source.sorting,
    };
};

const getActivitiesPayload = ({ filters, table, source, idList }) => {
    return {
        filter: {
            filters,
        },
        id: idList,
        sortingRequestList: source.sorting,
        paging: { ...table.pagination, page: table.pagination.page - 1 },
    };
};

const createExportDelayDriverToCsvPayload = ({ filters, latestVersionId }) => {
    return ({ title, subTitle }) => {
        return {
            fileMetaDataId: latestVersionId,
            filter: filters,
            fileName: `${title}_${subTitle}`,
        };
    };
};
